/**
 * Token Refresh — MangoTV
 * Pattern: mirrors glotv refreshToken.js
 *
 * Called by the axios response interceptor on 401 errors.
 * Attempts V2 refresh, falls back to V1 if that fails.
 *
 * Uses a lock to prevent multiple simultaneous refresh requests
 * when several API calls fail with 401 at the same time.
 */

import {
  refreshTokenV2,
  refreshTokenV1,
  getRefreshToken,
  storeAuthTokens,
  clearAuthTokens,
} from "./authApis";

// Prevent concurrent refresh attempts
let refreshPromise = null;

/**
 * Refresh the access token. Tries V2 first, falls back to V1.
 * If a refresh is already in progress, returns the existing promise.
 *
 * @returns {Promise<string>} — the new access token
 * @throws if refresh fails entirely
 */
export const refresh_token = async () => {
  // If already refreshing, return the existing promise
  if (refreshPromise) return refreshPromise;

  refreshPromise = _doRefresh();

  try {
    const result = await refreshPromise;
    return result;
  } finally {
    refreshPromise = null;
  }
};

const _doRefresh = async () => {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    clearAuthTokens();
    throw new Error("No refresh token available");
  }

  try {
    const response = await refreshTokenV2(refreshToken);
    if (response.status === "ok" && response.data) {
      storeAuthTokens(response.data);
      return response.data.access_token;
    }
    throw new Error("V2 refresh: unexpected response");
  } catch {
    // Fallback to V1
    try {
      const response = await refreshTokenV1(refreshToken);
      if (response.status === "ok" && response.data) {
        storeAuthTokens(response.data);
        return response.data.access_token;
      }
      throw new Error("V1 refresh: unexpected response");
    } catch {
      clearAuthTokens();
      throw new Error("Token refresh failed");
    }
  }
};
